import React, { useState } from 'react';
import { X, Loader2, Check, Sparkles } from 'lucide-react';
import paymentService from '../../services/paymentService';

/**
 * Exit-intent modal offering a 1-month Pro trial to unsubscribed users.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.proPlan - The Pro plan object from SubscriptionContext
 * @param {Object} props.user - The authenticated user object
 */
const ExitIntentProModal = ({ isOpen, onClose, proPlan, user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !proPlan) return null;

  const proFeatures = [
    'Up to 5 active job posts',
    'Up to 50 hires & communications/month',
    'Instant job post approval',
    'AI recommendations',
    'VA matchmaking access',
    'Interview scheduling',
    '10 AI job post generations/month',
  ];

  const handleSubscribe = async () => {
    if (!user) {
      window.location.href = '/login?redirect=/pricing';
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await paymentService.subscribeToplan({
        tier: 'pro',
        billingCycle: 'monthly',
        userId: user.id,
      });
    } catch (err) {
      console.error('Exit-intent checkout error:', err);
      setError(err.message || 'Failed to start checkout process');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-fade-in animate-slide-up">
        {/* Gradient header */}
        <div
          className="px-8 pt-8 pb-6 text-center"
          style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          <Sparkles className="h-10 w-10 text-yellow-300 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-white">
            Wait — Don't Miss Out!
          </h2>
          <p className="mt-2 text-indigo-100 text-sm">
            Try <span className="font-semibold text-white">Pro</span> for just 1 month and supercharge your hiring.
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {/* Price highlight */}
          <div className="flex items-baseline justify-center gap-1 mb-6">
            <span className="text-5xl font-extrabold text-gray-900">
              ${proPlan.monthly_price}
            </span>
            <span className="text-gray-500 text-lg">/month</span>
          </div>

          <p className="text-center text-sm text-gray-500 -mt-4 mb-6">
            1-month subscription &middot; cancel anytime
          </p>

          {/* Feature list */}
          <ul className="space-y-2.5 mb-6">
            {proFeatures.map((feature, i) => (
              <li key={i} className="flex items-center text-sm text-gray-700">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0 mr-2" />
                {feature}
              </li>
            ))}
          </ul>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 text-center mb-4">{error}</p>
          )}

          {/* CTA */}
          <button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full py-3 rounded-lg font-semibold text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                Processing…
              </span>
            ) : (
              'Start Pro — $' + proPlan.monthly_price + '/mo'
            )}
          </button>

          <button
            onClick={onClose}
            className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            No thanks, I'll stick with Free
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentProModal;
